import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

interface CreatableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  label?: string;
  className?: string;
  formatDisplay?: (value: string) => string;
}

export function CreatableSelect({
  value,
  onChange,
  options,
  placeholder = 'Sélectionner...',
  label,
  className,
  formatDisplay,
}: CreatableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newValue, setNewValue] = useState('');
  const createInputRef = useRef<HTMLInputElement>(null);

  // Helper to display formatted value
  const displayValue = (val: string) => formatDisplay ? formatDisplay(val) : val;

  // Auto-focus on create input when entering create mode
  useEffect(() => {
    if (isCreating) {
      setTimeout(() => {
        createInputRef.current?.focus();
      }, 0);
    }
  }, [isCreating]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setIsCreating(false);
    setNewValue('');
  };

  const handleCreate = () => {
    if (newValue.trim()) {
      handleSelect(newValue.trim());
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewValue('');
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newValue.trim()) {
      e.preventDefault();
      handleCreate();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setIsCreating(false);
      setNewValue('');
    }
  };

  return (
    <div className={cn('relative', className)}>
      {label && (
        <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      )}

      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              'flex items-center justify-between w-full h-10 px-3 py-2 rounded-md border border-input bg-background text-sm',
              'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
              'cursor-pointer hover:border-gray-400 transition-colors'
            )}
          >
            <span className={cn('flex-1', !value && 'text-muted-foreground')}>
              {value ? displayValue(value) : placeholder}
            </span>
            <ChevronDown className={cn(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'rotate-180'
            )} />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] max-h-60 overflow-auto"
          align="start"
          sideOffset={4}
        >
          {/* Existing options */}
          {options.length > 0 ? (
            options.map((option) => (
              <div
                key={option}
                className={cn(
                  'flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100',
                  value === option && 'bg-gray-50'
                )}
                onClick={() => handleSelect(option)}
              >
                <span>{displayValue(option)}</span>
                {value === option && <Check className="h-4 w-4 text-green-600" />}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-gray-500 text-sm">
              Aucune option disponible
            </div>
          )}

          {/* Create section */}
          <div className="border-t border-gray-100">
            {!isCreating ? (
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 text-blue-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCreating(true);
                }}
              >
                <Plus className="h-4 w-4" />
                <span>Créer nouveau</span>
              </div>
            ) : (
              <div className="p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    ref={createInputRef}
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyDown={handleCreateKeyDown}
                    placeholder="Nom de la nouvelle option..."
                    className="flex-1 h-8 px-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ fontSize: '16px' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreate();
                    }}
                    disabled={!newValue.trim()}
                    className={cn(
                      'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                      newValue.trim()
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    )}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelCreate();
                    }}
                    className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
